import { gql, TypedDocumentNode } from "@apollo/client";

import {
  AlertsPageRequest,
  AlertsSearchQuery,
  AlertsSearchQueryVariables,
  AlertValidationDecision,
} from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { alertSummaryFragment } from "src/graphql/anti-fraud/fragments/AlertSummaryFragment";
import { pageInfoFragment } from "src/graphql/anti-fraud/fragments/PageInfoFragment";
import { SearchParamsAlert } from "src/graphql/anti-fraud/queries/typings.ts";
import { useFetchQuery } from "src/graphql/utils";
import { mapToAlertSummary } from "src/mappers/alert.mappers";
import { isEmpty } from "src/utils/arrays.utils.ts";

export const searchAlertsQuery = gql`
  query alertsSearch(
    $searchString: String
    $alertStatus: AlertStatus
    $pagination: Pagination!
    $sorting: [AlertsSortingOrder!]
    $ruleIds: [ID!]
    $displayOnlyMyAlerts: Boolean
    $validationDecision: [AlertValidationDecision!]
    $caseTypeCodes: [String!]
    $assignedTo: String
  ) {
    alertsSearch(
      input: {
        searchString: $searchString
        alertStatus: $alertStatus
        pagination: $pagination
        sorting: $sorting
        ruleIds: $ruleIds
        displayOnlyMyAlerts: $displayOnlyMyAlerts
        validationDecision: $validationDecision
        caseTypeCodes: $caseTypeCodes
        assignedTo: $assignedTo
      }
    ) {
      alerts {
        ...AlertSummaryFragment
      }
      pageInfo {
        ...PageInfo
      }
    }
  }

  ${alertSummaryFragment}
  ${pageInfoFragment}
` as TypedDocumentNode<AlertsSearchQuery, AlertsSearchQueryVariables>;

export const useSearchAlertsQuery = (
  options: SearchParamsAlert & {
    assignedTo?: string;
    displayOnlyMyAlerts?: boolean;
    decisions?: AlertValidationDecision[];
  }
) => {
  // if searchString is empty, send undefined to the query
  return useFetchQuery(searchAlertsQuery, {
    queryOptions: {
      variables: {
        searchString: options.searchTerm || null,
        pagination: {
          offset: options.offset,
          limit: options.limit,
        },
        alertStatus: options.alertStatus ?? null,
        sorting: options.sorting ?? null,
        ruleIds: options.ruleIds,
        displayOnlyMyAlerts: options.displayOnlyMyAlerts,
        validationDecision: options.decisions,
        caseTypeCodes: options.caseTypeCodes,
        assignedTo: options.assignedTo,
      } as AlertsPageRequest,
      fetchPolicy: "network-only",
    },
    toData: (data) =>
      data
        ? {
            alerts: data?.alertsSearch.alerts.map(mapToAlertSummary),
            pageInfo: data?.alertsSearch.pageInfo,
          }
        : null,
    isEmpty: (data) => isEmpty(data?.alertsSearch.alerts),
    uiErrors: {
      fetchError: "FETCH-ALERTS-ERROR",
    },
  });
};
